/**
 * Invoice Generation Service
 * Handles automatic invoice generation for orders
 *
 * @author SAUBHAGYA Development Team
 */

// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');

function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get user ID from JWT token
 */
function getUserIdFromToken(): string {
  try {
    const token = getAuthToken();
    if (!token) return 'SYSTEM';

    // Decode JWT payload (second part)
    const base64Url = token.split('.')[1];
    if (!base64Url) return 'SYSTEM';

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload);
    return payload.sub || payload.userId || payload.id || 'SYSTEM';
  } catch {
    return 'SYSTEM';
  }
}

/**
 * Generate invoice for a created order
 * Call this after order creation to automatically create an invoice
 *
 * @param orderId - ID of the order for which to generate invoice
 * @param createdBy - User ID creating the invoice (optional, extracted from token if not provided)
 * @returns Invoice details if successful
 * @throws Error if invoice generation fails
 */
export async function generateInvoiceForOrder(orderId: number, createdBy?: string): Promise<any> {
  try {
    const token = getAuthToken();

    if (!token) {
      console.warn('[InvoiceGenerationService] No auth token available for invoice generation');
      return null;
    }

    const userId = createdBy || getUserIdFromToken();
    console.log('[InvoiceGenerationService] Generating invoice for order:', orderId, 'by user:', userId);

    const response = await fetch(
      `${COMMERCE_SERVICE_URL}/api/v1/invoices/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: orderId,
          createdBy: userId,
        }),
      }
    );

    // Log response status
    console.log('[InvoiceGenerationService] Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || 'Failed to generate invoice';
      console.warn('[InvoiceGenerationService] Invoice generation warning:', errorMessage);
      // Don't throw - invoice generation is secondary to order creation
      return null;
    }

    const data = await response.json();
    console.log('[InvoiceGenerationService] Invoice generated successfully:', data);

    return data;
  } catch (error) {
    console.error('[InvoiceGenerationService] Error generating invoice:', error);
    // Silently fail - don't block order creation if invoice generation fails
    return null;
  }
}

/**
 * Auto-generate invoice after order creation (non-blocking)
 * Runs invoice generation in the background without blocking the main flow
 *
 * @param orderId - ID of the created order
 */
export function autoGenerateInvoice(orderId: number): void {
  // Fire and forget - don't wait for invoice generation
  generateInvoiceForOrder(orderId).catch((error) => {
    console.error('[InvoiceGenerationService] Background invoice generation failed:', error);
    // Still don't throw - this is a background operation
  });
}

export default {
  generateInvoiceForOrder,
  autoGenerateInvoice,
};
